import { useRef, useState } from "react";

import { identityApiRef, useApi } from "@backstage/core-plugin-api";
import { Alert, ButtonIcon } from "@backstage/ui";

import { AskjackRef } from "../api";
import {
    useFocusOnVisible,
    useIdentityContext,
    useMessages,
    useScrollToBottom,
} from "../hooks";
import styles from "./ChatBody.module.css";
import { ChatInput } from "./ChatInput";
import { MessageList } from "./MessageList";

// ── ChatBody ───────────────────────────────────────────────────────────────────

export interface ChatBodyProps {
    /** Vertically centers the empty-state prompt. Useful for full-page layouts. */
    centerEmptyState?: boolean;
    /** When toggled to true, focuses the input field. */
    isVisible?: boolean;
}

export const ChatBody = ({
    centerEmptyState = false,
    isVisible = false,
}: ChatBodyProps) => {
    const [input, setInput] = useState("");
    const askJackApi = useApi(AskjackRef);
    const identityApi = useApi(identityApiRef);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);

    const identity = useIdentityContext(identityApi);
    const { messages, error, loading, dismissError, sendMessage } = useMessages(
        identity,
        askJackApi
    );

    useScrollToBottom(messages, messagesEndRef);
    useFocusOnVisible(isVisible, inputRef);

    const handleSend = async () => {
        const current = input;
        setInput("");
        await sendMessage(current);
    };

    return (
        <>
            {!!error && (
                <Alert
                    status="danger"
                    description={error}
                    className={styles.alertError}
                    customActions={
                        <ButtonIcon
                            icon={<span aria-hidden>✕</span>}
                            aria-label="Dismiss error"
                            onPress={dismissError}
                            variant="tertiary"
                            size="small"
                        />
                    }
                />
            )}
            <MessageList
                messages={messages}
                endRef={messagesEndRef}
                centerEmptyState={centerEmptyState}
                loading={loading}
            />
            <ChatInput
                value={input}
                loading={loading}
                onChange={setInput}
                inputRef={inputRef}
                onSend={handleSend}
            />
        </>
    );
};
